import axios, { InternalAxiosRequestConfig } from 'axios';
import { API_TIMEOUT } from '../../APIConstants';
import {
  applyAGraphResponseTransformations,
  getAGraphBaseUrl,
  ResponseTransformConfig,
  setAGraphHeaders,
} from '../../utils';
import { AGraphApiSettings } from '../Base';
import { conditionalParam, isAGraphEnabled } from './AGraphUtils';
import { AW_USER_PREFERENCES_API_URL, AW_USER_PREFERENCES_API_URL_AGRAPH } from './Constants';

export const userPreferenceSettings: AGraphApiSettings & {
  getPkgFn?: () => string | undefined;
} = {};

// Configure response transformations for AGraph API
const AGRAPH_RESPONSE_TRANSFORMS: ResponseTransformConfig[] = [
  {
    urlPattern: '/v1/{actions-filters,domain-filters,filters,prd-filters}{,/**}',
    method: 'GET',
    timestampFields: ['createdAt', 'updatedAt'],
    enumPrefixesToRemove: [
      {
        fieldNames: ['operator'],
        prefix: 'FILTER_OPERATOR_',
        recursiveFieldPatterns: ['filters'],
      },
      {
        fieldNames: ['valueType'],
        prefix: 'FILTER_VALUE_TYPE_',
        recursiveFieldPatterns: ['filters'],
      },
    ],
  },
  {
    urlPattern: '/v1/exposures{,/**}',
    method: 'GET',
    timestampFields: ['createdAt', 'updatedAt'],
    enumPrefixesToRemove: [
      {
        fieldNames: ['operator'],
        prefix: 'FILTER_OPERATOR_',
        recursiveFieldPatterns: ['selectedFilters'],
      },
      {
        fieldNames: ['valueType'],
        prefix: 'FILTER_VALUE_TYPE_',
        recursiveFieldPatterns: ['selectedFilters'],
      },
    ],
  },
  {
    urlPattern:
      '/v1/{booksync-preferences,deep-dive-proposal-tabs,proposal-preferences,recent-searches,report-overrides,selected-pages,{tou,uap-tou}-acknowledgements}{,/**}',
    method: 'GET',
    timestampFields: ['createdAt', 'updatedAt'],
  },
  {
    urlPattern: '/v1/{booksync-preferences/*,prd-user-attributes,{tou,uap-tou}-acknowledgements}',
    timestampFields: ['createdAt', 'updatedAt'],
  },
];

export const userPreferenceApi = axios.create({
  timeout: API_TIMEOUT,
  params: {},
});

userPreferenceApi.interceptors.request.use((config) => {
  const { getAladdinApiKey, getConfigurationDirectoryFn, getEnvTypeFn } = userPreferenceSettings;
  const envType = getEnvTypeFn?.();
  const configurationDirectory = getConfigurationDirectoryFn?.();
  if (!userPreferenceSettings.getPkgFn) {
    throw Error('You must define a getPkgFn function before using userPreferenceApi');
  }
  const params: Record<string, unknown> = config.params ?? {};
  const pkg = params.pkg ?? userPreferenceSettings.getPkgFn();
  const enableAGraph = isAGraphEnabled();
  const baseURL = enableAGraph ? getAGraphBaseUrl(AW_USER_PREFERENCES_API_URL_AGRAPH) : AW_USER_PREFERENCES_API_URL;
  const updatedConfig: InternalAxiosRequestConfig = {
    ...config,
    baseURL,
    params: {
      ...params,
      tenant: pkg,
      configurationDirectory: conditionalParam(config.url, 'configurationDirectory', configurationDirectory),
    },
  };
  setAGraphHeaders(updatedConfig, getAladdinApiKey?.(), envType);
  if (enableAGraph) {
    /*
     * We set the `tenant` param in the interceptor above - but it's still passing `pkg` too.
     * AWUserPreferenceServer ignores extra query params, but AGraph will fail validation, so we need to delete it here.
     */
    const updatedParams: Record<string, unknown> = updatedConfig.params;
    if (updatedParams.pkg) {
      delete updatedParams.pkg;
    }
  }
  return updatedConfig;
});

// Apply response transformations for AGraph API
userPreferenceApi.interceptors.response.use((response) => {
  if (isAGraphEnabled() && response.data) {
    applyAGraphResponseTransformations(response, AGRAPH_RESPONSE_TRANSFORMS);
  }

  return response;
});
